import {textToHtml} from '@frontapp/editor-html';

import {isHtml} from '../../../libs/shared/helpers/src/utilities/stringHelpers';
import {ChannelTypesEnum} from '../../models/api/channelCommonTypes';
import {buildContactUid, ContactSourcesEnum} from '../../models/api/contactModel';
import {ParticipantModel, ParticipantTypesEnum} from '../../models/api/participantModel';

// TODO: remove after shipping server-side normalization of calendar event descriptions
export function buildCalendarEventDescriptionHtml(
  description: string | undefined,
  channelType: ChannelTypesEnum | undefined,
) {
  if (!description) {
    return '';
  }

  if (!isHtml(description)) {
    return textToHtml(description);
  }

  if (channelType === ChannelTypesEnum.GMAIL) {
    // Google calendar event descriptions contain newline characters where a <br> tag is expected.
    // Therefore, we replace newlines with <br> tags. This is not done for O365 since the O365
    // event description is a complete HTML document with <br> tags where necessary.
    return description.replace(/(?:\r\n|\r|\n)/g, '<br>');
  }

  return description;
}

// Google's "Out of Office" and "Focus Time" events use this address as the organizer.
// Furthermore, because one cannot add other participants for these types of events, we can safely
// determine that this is a proxy address for the actual organizer.
const GCAL_PROXY_ORGANIZER = buildContactUid(
  ContactSourcesEnum.EMAIL,
  'unknownorganizer@calendar.google.com',
);

export function isProxyOrganizer(contactUid: string) {
  return contactUid === GCAL_PROXY_ORGANIZER;
}

// Compute the number of non-organizer and non-resource participants.
// This is used to determine whether rescheduling or delete confirmation dialogs are necessary when editing an event.
export function computeNonResourceGuestCount(participants: ReadonlyArray<ParticipantModel>): number {
  const hasProxyOrganizer = participants.find((p) => isProxyOrganizer(p.contactUid));

  // Events with proxy organizers aren't allowed to have guests.
  if (hasProxyOrganizer) {
    return 0;
  }

  const nonOrganizerNonResourceAttendees = participants.filter(
    (p) => !p.isOrganizer && p.isAttendee && p.type !== ParticipantTypesEnum.RESOURCE,
  );
  return nonOrganizerNonResourceAttendees.length;
}
